import React, { useCallback, useEffect, useState } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import {
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormHelperText,
    Typography,
    IconButton,
    Grid,
    InputAdornment,
    InputBasePropsSizeOverrides,
    CircularProgress,
    Checkbox,
    ListItemText,
    Button,
    Box,
    OutlinedInput,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import http from '../../commonFunctions/httpClient';
import { OverridableStringUnion } from '@mui/types';
import store from '../../StateManagement/store';

type InputLabelPropsSizeOverrides = {};
type ControllSize = OverridableStringUnion<'small' | 'normal', InputLabelPropsSizeOverrides>
type SelectSize = OverridableStringUnion<'small' | 'medium', InputBasePropsSizeOverrides>;

interface ControlledSelectFieldProps {
    label: any;
    id: string;
    name: string;
    className?: string;
    variant?: 'standard' | 'filled' | 'outlined';
    disabled?: boolean;
    control: any;
    defaultValue?: any;
    rules?: UseControllerProps['rules'];
    onChange?: (event: any) => void;
    required?: boolean;
    setValue?: any;
    reset?: any;
    type?: any;
    options?: { value: string | number; label: string; disabled?: boolean }[];
    description?: IDescription;
    labelType?: 'Material' | 'Normal';
    addButton?: boolean;
    handleAddButtonClick?: (e: any) => void;
    source?: {
        url: string;
        pageSize: number;
        page: number;
        filter: any[];
        sort: string[];
    };
    transFormData?: (data: any) => any;
    controllSize?: ControllSize;
    selectSize?: SelectSize;
    isMobile?: boolean;
    sx?: any;
    multiSelect?: boolean; // <-- NEW PROP to enable multi-select
}

interface IDescription {
    text: string | any;
    align: 'Top' | 'Bottom';
    style?: React.CSSProperties;
    className?: string;
}

const WSMultiCheckBoxWithApply: React.FC<ControlledSelectFieldProps> = ({
    id,
    label,
    className,
    control,
    name,
    rules,
    variant = 'outlined',
    disabled = false,
    defaultValue = [],
    setValue,
    onChange,
    type,
    options,
    description,
    labelType = 'Material',
    addButton,
    handleAddButtonClick,
    source,
    transFormData,
    controllSize = "small",
    selectSize = "small",
    reset,
    isMobile = false,
    sx,
    multiSelect = true // default true for this implementation
}) => {
    const [loading, setLoading] = useState<boolean>(!!source);
    const [newOptions, setNewOptions] = useState<any>(options || []);
    const [open, setOpen] = useState(false);
    const [tempSelected, setTempSelected] = useState<any[]>(defaultValue || []);

    useEffect(() => {
        if (options) setNewOptions(options);
    }, [options]);

    useEffect(() => {
        if (reset) reset();
    }, [reset]);

    const fetchDataFromAPI = async (
        page: number,
        pageSize: number,
        filter: any[] = [],
        sort: any[] = []
    ) => {
        try {
            if (source) {
                const response: any = await http.post(source.url, {
                    page,
                    pageSize,
                    filter,
                    sort,
                });
                return response;
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const loadPageData = useCallback(async () => {
        try {
            const response = await fetchDataFromAPI(1, 100000, source?.filter, source?.sort || []);
            setLoading(false);
            setNewOptions(transFormData ? transFormData(response?.data) : response?.data);
        } catch (error) {
            console.error('Error loading page data:', error);
        }
    }, [source, id, transFormData]);

    useEffect(() => {
        if (source && loading) loadPageData();
    }, [loading]);

    useEffect(() => {
        const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (state.components[id]?.data === null) {
                loadPageData();
            }
        });
        return () => unsubscribe();
    }, [id, loadPageData]);

    const handleToggle = (item: any) => {
        setTempSelected((prev) =>
            prev.includes(item) ? prev.filter((v) => v !== item) : [...prev, item]
        );
    };

    const handleApply = (fieldOnChange: any) => {
        fieldOnChange(tempSelected);
        if (onChange) onChange({ target: { value: tempSelected } });
        setOpen(false);
    };

    const handleClick = (e: any) => {
        if (handleAddButtonClick) handleAddButtonClick(e);
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={rules}
            render={({ field, fieldState }) => (
                <FormControl
                    variant={variant}
                    className={className}
                    disabled={disabled}
                    fullWidth
                    error={!!fieldState.error}
                >
                    {description?.align === 'Bottom' && (
                        <Typography style={description.style} className={description?.className}>
                            {description.text}
                        </Typography>
                    )}

                    <Select
                        labelId={id}
                        id={id}
                        displayEmpty
                        multiple={multiSelect}
                        open={open}
                        hiddenLabel={true}
                        onClose={() => setOpen(false)}
                        onOpen={() => {
                            setTempSelected(field.value || []);
                            setOpen(true);
                        }}
                        value={field.value || []}
                        onChange={(e) => {
                            if (!multiSelect) {
                                field.onChange(e);
                                if (onChange) onChange(e);
                            }
                        }}

                        size={selectSize}
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return <Typography sx={{ fontSize: '0.875rem', color: 'gray' }}>{label}</Typography>;
                            }
                            return Array.isArray(selected)

                                ? selected
                                    .map((val) => {
                                        const option = newOptions.find((o: any) => o.value === val);
                                        return option?.label || val;
                                    })
                                    .join(', ')
                                : selected;
                        }}
                        input={
                            <OutlinedInput
                                notched={true} // important!
                                label={label}
                                sx={{
                                    position: 'relative',
                                    borderRadius: '50px',
                                    backgroundColor: '#fff',
                                    border: '0.5px solid #098963',
                                    fontSize: '0.75rem',
                                    height: 35,
                                    // Keep the notch outline but make border transparent so label floats properly
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                        //  borderWidth: 1,
                                        //    borderStyle: 'solid',
                                        //  borderColor: '#098963',
                                    },

                                    // // Your gradient border behind the outline
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: -1,
                                        left: -1,
                                        right: -1,
                                        bottom: -1,
                                        borderRadius: '50px',
                                        padding: '1px',
                                        background: 'linear-gradient(90deg, #098963 0%, #CEEEE5 100%)',
                                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                        WebkitMaskComposite: 'destination-out',
                                        maskComposite: 'exclude',
                                        pointerEvents: 'none',
                                        zIndex: -1,
                                    },
                                    '&:after': {
                                        border: 'none',
                                    },

                                }}
                            />


                        }
                        MenuProps={{
                            PaperProps: {
                                style: { padding: 8, minWidth: 200 },
                            },
                        }}
                    >
                        {newOptions.map((option: any) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                disabled={option?.disabled}
                                onClick={() => handleToggle(option.value)}
                            >

                                <Checkbox checked={tempSelected.includes(option.value)} sx={{
                                    color: '#098963',
                                    '&.Mui-checked': {
                                        color: '#098963',
                                    },
                                    '& svg': {
                                        fontSize: '0.875rem', // ✅ actual checkbox size
                                    },
                                }} />
                                <ListItemText primary={option.label} primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                }} />
                            </MenuItem>
                        ))}

                        <Box px={2} pt={1}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => handleApply(field.onChange)}
                                sx={{
                                    height:30,
                                    fontSize: '0.75rem', // ✅ smaller font size
                                    fontWeight: 500,
                                    borderRadius: 999,
                                    backgroundColor: '#098963',
                                    paddingY: 1, // optional: reduce vertical padding
                                    '&:hover': {
                                        backgroundColor: '#066e4f',
                                    },
                                }}
                            >
                                Apply
                            </Button>
                        </Box>
                    </Select>

                    {loading && (
                        <CircularProgress
                            size={24}
                            style={{
                                position: 'absolute',
                                right: '20px',
                                top: '30%',
                                transform: 'translateY(-50%)',
                            }}
                        />
                    )}
                    {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl >
            )}
        />
    );
};

export default WSMultiCheckBoxWithApply;
